/**
 * PlayerWidget - Creates and manages the floating player controls
 * Handles play/pause, navigation, settings, and close buttons
 */
import DOMHelper from "./DOMHelper.js";
import { t } from "../core/i18n.js";

export default class PlayerWidget {
	/** @type {HTMLElement|null} Player widget DOM element */
	#element = null;

	/** @type {Function|null} Callback function for button events */
	#onCallback = null;

	/** @type {Function|null} Callback for settings toggle */
	#onToggleSettings = null;
	/**
	 * Initialize PlayerWidget
	 * @param {Function} onCallback - Callback function for button events
	 * @param {Function} onToggleSettings - Callback for settings toggle
	 */
	constructor(onCallback, onToggleSettings) {
		this.#onCallback = onCallback;
		this.#onToggleSettings = onToggleSettings;
	}

	/**
	 * Create the player widget element
	 * @param {Object} initialState - Initial player state
	 * @returns {HTMLElement} The created widget element
	 */
	create(initialState = {}) {
		this.#element = DOMHelper.createElement("div", "tjs-player-widget");

		// Create controls container
		const controlsDiv = DOMHelper.createElement(
			"div",
			"tjs-player-controls",
		);

		// Create buttons using DOM methods
		const buttons = [
			{
				class: "tjs-prev-btn",
				title: t("player.buttons.previous"),
				icon: "⏮️",
			},
			{
				class: "tjs-play-btn",
				title: t("player.buttons.play"),
				icon: "▶️",
				hasProgress: true,
			},
			{
				class: "tjs-next-btn",
				title: t("player.buttons.next"),
				icon: "⏭️",
			},
			{
				class: "tjs-settings-btn",
				title: t("player.buttons.settings"),
				icon: "⚙️",
			},
			{
				class: "tjs-close-btn",
				title: t("player.buttons.close"),
				icon: "✖️",
			},
		];

		buttons.forEach((buttonConfig) => {
			// Create button with icon using DOM methods
			const button = DOMHelper.createButton(
				buttonConfig.class,
				buttonConfig.title,
				buttonConfig.icon,
			);

			const children = [button];
			if (buttonConfig.hasProgress) {
				children.push(
					DOMHelper.createElement("div", "tjs-progress-container"),
				);
			}

			const buttonContainer = DOMHelper.createContainer(
				"tjs-button-container",
				children,
			);
			controlsDiv.appendChild(buttonContainer);
		});

		this.#element.appendChild(controlsDiv);
		this.#addEventListeners();
		this.updateState(initialState);

		return this.#element;
	}

	/**
	 * Add event listeners to player controls
	 */
	#addEventListeners() {
		if (!this.#element) return;

		const playBtn = this.#element.querySelector(".tjs-play-btn");
		const prevBtn = this.#element.querySelector(".tjs-prev-btn");
		const nextBtn = this.#element.querySelector(".tjs-next-btn");
		const settingsBtn = this.#element.querySelector(".tjs-settings-btn");
		const closeBtn = this.#element.querySelector(".tjs-close-btn");

		playBtn?.addEventListener("click", (e) => {
			e.preventDefault();
			e.stopPropagation();
			this.#onCallback("onPlay");
		});
		prevBtn?.addEventListener("click", (e) => {
			e.preventDefault();
			e.stopPropagation();
			this.#onCallback("onPrevious");
		});
		nextBtn?.addEventListener("click", (e) => {
			e.preventDefault();
			e.stopPropagation();
			this.#onCallback("onNext");
		});
		settingsBtn?.addEventListener("click", (e) => {
			e.preventDefault();
			e.stopPropagation();
			this.#onToggleSettings();
		});
		closeBtn?.addEventListener("click", (e) => {
			e.preventDefault();
			e.stopPropagation();

			this.#onCallback("onClose");
		});
	}

	/**
	 * Update player widget state
	 * @param {Object} state - New state
	 */
	updateState(state) {
		if (!this.#element) return;

		const playButton = this.#element.querySelector(".tjs-play-btn");
		const playIcon = playButton?.querySelector(".tjs-icon");

		if (playIcon) {
			const isActuallyPaused = !!state.isPaused;
			const showPause = state.isPlaying && !isActuallyPaused;
			playIcon.textContent = showPause ? "⏸️" : "▶️";
			playButton.title = showPause
				? t("player.buttons.pause")
				: t("player.buttons.play");
		}

		// Update progress if available
		if (
			state.currentSegment !== undefined &&
			state.totalSegments !== undefined
		) {
			const progressContainer = this.#element.querySelector(
				".tjs-progress-container",
			);
			if (progressContainer) {
				let progressText =
					progressContainer.querySelector(".tjs-progress");
				if (!progressText) {
					// Create progress indicator in the progress container
					progressText = document.createElement("span");
					progressText.className = "tjs-progress";
					progressContainer.appendChild(progressText);
				}
				progressText.textContent = `${state.currentSegment + 1}/${state.totalSegments}`;
			}
		}
	}

	/**
	 * Show the widget
	 */
	show() {
		if (this.#element) {
			this.#element.style.display = "";
		}
	}

	/**
	 * Hide the widget
	 */
	hide() {
		if (this.#element) {
			this.#element.style.display = "none";
		}
	}

	/**
	 * Remove the widget from DOM
	 */
	destroy() {
		if (this.#element) {
			this.#element.remove();
			this.#element = null;
		}
	}

	/**
	 * Get the widget element
	 * @returns {HTMLElement|null} The widget element
	 */
	getElement() {
		return this.#element;
	}
}
